import {
  SerializedRequestBody,
  SerializedFormData,
  SerializedHeader,
  SerializedBlob,
  RequestOptions,
  RequestBody,
  SerializedRequestOptions,
  SerializedResponse,
} from './types';
import { mergeBuffers, readBlobAsDataUri, uint8ArrayToByteString } from './utils';

export const serializeHeaders = (headers: HeadersInit): SerializedHeader => {
  if (!(headers instanceof Headers)) {
    return headers;
  }

  const serializedHeaders: Record<string, string> = {};

  for (const [name, value] of headers.entries()) {
    serializedHeaders[name] = value.toString();
  }

  return serializedHeaders;
};

export const serializeBlob = async (file: File | Blob): Promise<SerializedBlob> => {
  const dataUri = await readBlobAsDataUri(file);

  return {
    type: file.type,
    size: file.size,
    content: dataUri,
    ...(file instanceof File ? { name: file.name } : {}),
  };
};

export const serializeFormData = async (data: FormData): Promise<SerializedFormData> => {
  const res: SerializedFormData = {};

  for (const [key, value] of data.entries()) {
    if (!res[key]) {
      res[key] = [];
    }

    const val = value instanceof File ? await serializeBlob(value) : value.toString();

    res[key].push(val);
  }
  return res;
};

export const serializeRequestBody = async (body: RequestBody): Promise<SerializedRequestBody> => {
  if (body === null) return null;

  if (body instanceof Blob) {
    return {
      type: 'Blob',
      content: await serializeBlob(body),
    };
  }

  if (body instanceof URLSearchParams) {
    return {
      type: 'URLSearchParams',
      content: body.toString(),
    };
  }

  if (body instanceof FormData) {
    return {
      type: 'FormData',
      content: await serializeFormData(body),
    };
  }

  return {
    type: 'string',
    content: body.toString(),
  };
};

export const serializeResponseBody = async (body: Response['body']): Promise<string | null> => {
  if (!body) return null;

  const reader = body.getReader();

  let result = new Uint8Array();
  let value: Uint8Array | undefined;
  do {
    ({ value } = await reader.read());
    if (value) {
      result = mergeBuffers(result, value);
    }
  } while (value);

  return uint8ArrayToByteString(result);
};

export const serializeResponse = async (response: Response): Promise<SerializedResponse> => {
  const serializedBody = await serializeResponseBody(response.body);
  return {
    ok: response.ok,
    redirected: response.redirected,
    status: response.status,
    statusText: response.statusText,
    type: response.type,
    url: response.url,
    headers: serializeHeaders(response.headers),
    body: serializedBody,
  };
};

export const serializeRequestOptions = async (options: RequestOptions): Promise<SerializedRequestOptions> => {
  const { body, headers, ...rest } = options;

  return {
    ...rest,
    ...(headers ? { headers: serializeHeaders(headers) } : {}),
    ...(body !== undefined ? { body: await serializeRequestBody(body) } : {}),
  };
};
